import { create } from "zustand";
import { ApiError, fetchJSON, readCsrfToken } from "@/lib/api";

export type SessionUser = { id: string; email: string; createdAt: string };

type AuthState = {
  user: SessionUser | null;
  isLoading: boolean;
  error?: string | null;
  setUser: (user: SessionUser | null) => void;
  bootstrap: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (token: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
};

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isLoading: false,
  error: null,
  setUser: (user) => set({ user }),
  bootstrap: async () => {
    set({ isLoading: true, error: null });
    const csrf = readCsrfToken();
    try {
      const user = await fetchJSON<SessionUser>("/auth/me");
      set({ user, isLoading: false });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        if (!csrf) {
          set({ user: null, isLoading: false, error: null });
          return;
        }
        try {
          const refreshed = await fetchJSON<SessionUser>("/auth/refresh", {
            method: "POST",
          });
          set({ user: refreshed, isLoading: false });
          return;
        } catch {
          set({ user: null, isLoading: false, error: null });
          return;
        }
      }
      set({
        isLoading: false,
        error: err instanceof Error ? err.message : "Failed to load session",
      });
    }
  },
  login: async (email, password) => {
    set({ isLoading: true, error: null });
    try {
      const user = await fetchJSON<SessionUser>("/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, password }),
      });
      set({ user, isLoading: false });
    } catch (err) {
      set({ isLoading: false });
      throw err;
    }
  },
  register: async (email, password) => {
    set({ isLoading: true, error: null });
    try {
      const user = await fetchJSON<SessionUser>("/auth/register", {
        method: "POST",
        body: JSON.stringify({ email, password }),
      });
      set({ user, isLoading: false });
    } catch (err) {
      set({ isLoading: false });
      throw err;
    }
  },
  forgotPassword: async (email) => {
    set({ isLoading: true, error: null });
    try {
      await fetchJSON<{ success: boolean }>("/auth/forgot-password", {
        method: "POST",
        body: JSON.stringify({ email }),
      });
      set({ isLoading: false });
    } catch (err) {
      set({ isLoading: false });
      throw err;
    }
  },
  resetPassword: async (token, password) => {
    set({ isLoading: true, error: null });
    try {
      await fetchJSON<{ success: boolean }>("/auth/reset-password", {
        method: "POST",
        body: JSON.stringify({ token, password }),
      });
      set({ isLoading: false });
    } catch (err) {
      set({ isLoading: false });
      throw err;
    }
  },
  logout: async () => {
    set({ isLoading: true, error: null });
    try {
      await fetchJSON<{ success: boolean }>("/auth/logout", {
        method: "POST",
      });
    } catch (err) {
      // ignore logout errors
    }
    set({ user: null, isLoading: false });
  },
}));
